import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Box,
    CircularProgress,
    Switch,
    FormControlLabel,
    Divider,
    Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { path } from "../../../routes";

const EditDisplay_modal = (props) => {
    const allowKioskSettings = props.allowKioskSettings !== false; // default true (admin / backward compat)
    const isKiosk = Number(props.display?.device_group) === 2;
    const showRuntimeSection = allowKioskSettings || !isKiosk;

    const [name, setName] = useState(props.display?.description || "");
    const [type, setType] = useState(props.display?.device_group || "");
    const [roomGroup, setRoomGroup] = useState(props.display?.roomGroup ?? null);
    const [loading, setLoading] = useState(false);
    const [groups, setGroups] = useState([]);
    
    // URL parameters
    const [urlId, setUrlId] = useState(props.display?.url_id ?? "");
    const [urlRoom, setUrlRoom] = useState(props.display?.url_room ?? "");
    const [urlOrgID, setUrlOrgID] = useState(props.display?.url_orgID ?? "");
    const [urlKiosk, setUrlKiosk] = useState(props.display?.url_kiosk ?? "");

    // Runtime / kiosk settings (saved per device in backend)
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [objectFit, setObjectFit] = useState('contain'); // contain | cover
    const [showLogo, setShowLogo] = useState(true);
    const [backgroundColor, setBackgroundColor] = useState('#012e5f');
    const [roomNameColor, setRoomNameColor] = useState('#ffffff');
    const [meetingTitleColor, setMeetingTitleColor] = useState('#ffffff');
    const [showTitle, setShowTitle] = useState(true);
    const [showTimeRange, setShowTimeRange] = useState(false);
    const [slideshowPriority, setSlideshowPriority] = useState(false);
    const [demoRatio, setDemoRatio] = useState('9:16'); // 9:16 | 16:9 | 4:3
    const [videoGroupId, setVideoGroupId] = useState('');
    const [slideshowGroupId, setSlideshowGroupId] = useState('');
    const [slideshowGroupIds, setSlideshowGroupIds] = useState([]);
    const [displayArrows, setDisplayArrows] = useState({});
    const [copyRuntimeFromId, setCopyRuntimeFromId] = useState('');
    const [displaysList, setDisplaysList] = useState([]);

    useEffect(() => {
        if (props.display) {
            setName(props.display.description || "");
            setType(props.display.device_group || "");
            // roomGroup must be a number (groupId); if API returns string like 'A-D', use null
            const rg = props.display.roomGroup;
            const rgNum = (rg != null && rg !== '') ? parseInt(String(rg).trim(), 10) : null;
            setRoomGroup((rgNum != null && !isNaN(rgNum) && isFinite(rgNum)) ? rgNum : null);
            // Convert URL parameters to strings for input fields
            // null/undefined -> empty string, numbers -> string
            // If url_room is not set, default to device id
            const urlIdVal = props.display.url_id != null && props.display.url_id !== undefined ? String(props.display.url_id) : "";
            const urlRoomVal = (props.display.url_room != null && props.display.url_room !== undefined) 
                ? String(props.display.url_room) 
                : (props.display.id ? String(props.display.id) : "");
            const urlOrgIDVal = props.display.url_orgID != null && props.display.url_orgID !== undefined ? String(props.display.url_orgID) : "";
            const urlKioskVal = props.display.url_kiosk != null && props.display.url_kiosk !== undefined ? String(props.display.url_kiosk) : "";
            
            // Debug logging
            console.log('[EditDisplay_modal] Loading display data:', {
                id: props.display.id,
                url_id: props.display.url_id,
                url_room: props.display.url_room,
                url_orgID: props.display.url_orgID,
                url_kiosk: props.display.url_kiosk,
                converted: { urlIdVal, urlRoomVal, urlOrgIDVal, urlKioskVal }
            });
            
            setUrlId(urlIdVal);
            setUrlRoom(urlRoomVal);
            setUrlOrgID(urlOrgIDVal);
            setUrlKiosk(urlKioskVal);
        }
    }, [props.display]);

    useEffect(() => {
        const orgId = props.orgId || props.display?.cOrganization_id;
        if (!orgId) return;
        let cancelled = false;
        axios.get(`${path}/getSlideGroup?orgID=${orgId}`)
            .then((res) => {
                if (!cancelled && Array.isArray(res.data)) setGroups(res.data);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [props.orgId, props.display?.cOrganization_id]);

    // Načíst seznam všech displayů v organizaci pro dropdown "Copy from device"
    useEffect(() => {
        const orgId = props.orgId || props.display?.cOrganization_id;
        if (!orgId) return;
        let cancelled = false;
        Promise.all([
            axios.get(`${path}/getDisplaysbyOrg/${orgId}`).catch(() => ({ data: [] })),
            axios.get(`${path}/getDeviceV2?orgID=${orgId}`).catch(() => ({ data: [] }))
        ])
            .then(([displaysRes, kiosksRes]) => {
                if (cancelled) return;
                const displays = Array.isArray(displaysRes.data) ? displaysRes.data : [];
                const kiosks = Array.isArray(kiosksRes.data) ? kiosksRes.data : [];
                const allDevices = [...displays, ...kiosks];
                setDisplaysList(allDevices);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [props.orgId, props.display?.cOrganization_id]);

    useEffect(() => {
        const deviceId = props.display?.id;
        if (!deviceId) return;
        let cancelled = false;
        const normalize = (s) => {
            const out = {};
            if (s?.objectFit === 'contain' || s?.objectFit === 'cover') out.objectFit = s.objectFit;
            if (typeof s?.showLogo === 'boolean') out.showLogo = s.showLogo;
            if (typeof s?.backgroundColor === 'string' && s.backgroundColor) out.backgroundColor = s.backgroundColor;
            if (typeof s?.roomNameColor === 'string' && s.roomNameColor) out.roomNameColor = s.roomNameColor;
            else if (typeof s?.textColor === 'string' && s.textColor) out.roomNameColor = s.textColor;
            if (typeof s?.meetingTitleColor === 'string' && s.meetingTitleColor) out.meetingTitleColor = s.meetingTitleColor;
            else if (typeof s?.textColor === 'string' && s.textColor) out.meetingTitleColor = s.textColor;
            if (typeof s?.showTitle === 'boolean') out.showTitle = s.showTitle;
            if (typeof s?.showTimeRange === 'boolean') out.showTimeRange = s.showTimeRange;
            if (typeof s?.slideshowPriority === 'boolean') out.slideshowPriority = s.slideshowPriority;
            if (typeof s?.demoRatio === 'string' && /^\d+\s*:\s*\d+$/.test(s.demoRatio)) out.demoRatio = String(s.demoRatio);
            if (typeof s?.videoGroupId === 'number' && Number.isFinite(s.videoGroupId) && s.videoGroupId > 0) out.videoGroupId = Math.floor(s.videoGroupId);
            if (typeof s?.slideshowGroupId === 'number' && Number.isFinite(s.slideshowGroupId) && s.slideshowGroupId > 0) out.slideshowGroupId = Math.floor(s.slideshowGroupId);
            if (s?.displayArrows && typeof s.displayArrows === 'object') out.displayArrows = s.displayArrows;
            return out;
        };
        const load = async () => {
            setSettingsLoading(true);
            try {
                const orgId = props.orgId || props.display?.cOrganization_id;
                const url = orgId ? `${path}/getDeviceSettings/${deviceId}?orgID=${encodeURIComponent(orgId)}` : `${path}/getDeviceSettings/${deviceId}`;
                const resp = await axios.get(url);
                const s = normalize(resp.data?.settings || {});
                if (cancelled) return;
                if (s.objectFit) setObjectFit(s.objectFit);
                if (typeof s.showLogo === 'boolean') setShowLogo(s.showLogo);
                if (s.backgroundColor) setBackgroundColor(s.backgroundColor);
                else setBackgroundColor('#012e5f');
                if (s.roomNameColor) setRoomNameColor(s.roomNameColor);
                else if (s.textColor) setRoomNameColor(s.textColor);
                else setRoomNameColor('#ffffff');
                if (s.meetingTitleColor) setMeetingTitleColor(s.meetingTitleColor);
                else if (s.textColor) setMeetingTitleColor(s.textColor);
                else setMeetingTitleColor('#ffffff');
                if (typeof s.showTitle === 'boolean') setShowTitle(s.showTitle);
                if (typeof s.showTimeRange === 'boolean') setShowTimeRange(s.showTimeRange);
                else setShowTimeRange(false);
                if (typeof s.slideshowPriority === 'boolean') setSlideshowPriority(s.slideshowPriority);
                else setSlideshowPriority(false);
                if (typeof s.demoRatio === 'string') setDemoRatio(s.demoRatio);
                if (typeof s.videoGroupId === 'number') setVideoGroupId(String(s.videoGroupId));
                else setVideoGroupId('');
                if (typeof s.slideshowGroupId === 'number') setSlideshowGroupId(String(s.slideshowGroupId));
                else setSlideshowGroupId('');
                if (Array.isArray(s.slideshowGroupIds)) {
                    setSlideshowGroupIds(
                        s.slideshowGroupIds
                            .map((x) => Number(x))
                            .filter((n) => Number.isFinite(n) && n > 0)
                            .map((n) => Math.floor(n))
                    );
                } else {
                    setSlideshowGroupIds([]);
                }
                if (s.displayArrows && typeof s.displayArrows === 'object') setDisplayArrows(s.displayArrows);
                else setDisplayArrows({});
            } catch (e) {
                // ignore (device may not have settings yet)
            } finally {
                if (!cancelled) setSettingsLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.display?.id]);

    const handleCopyRuntimeFromDevice = async (sourceDeviceId) => {
        const sourceId = sourceDeviceId ? Number(sourceDeviceId) : Number(copyRuntimeFromId);
        if (!Number.isFinite(sourceId) || sourceId <= 0) {
            alert('Select a device to copy from.');
            return;
        }
        try {
            const orgId = props.orgId || props.display?.cOrganization_id;
            const url = orgId
                ? `${path}/getDeviceSettings/${sourceId}?orgID=${encodeURIComponent(orgId)}`
                : `${path}/getDeviceSettings/${sourceId}`;
            const resp = await axios.get(url);
            const s = resp.data?.settings || {};
            if (s.objectFit === 'contain' || s.objectFit === 'cover') setObjectFit(s.objectFit);
            if (typeof s.showLogo === 'boolean') setShowLogo(s.showLogo);
            if (typeof s.backgroundColor === 'string' && s.backgroundColor) setBackgroundColor(s.backgroundColor);
            if (typeof s.roomNameColor === 'string' && s.roomNameColor) setRoomNameColor(s.roomNameColor);
            else if (typeof s.textColor === 'string' && s.textColor) setRoomNameColor(s.textColor);
            if (typeof s.meetingTitleColor === 'string' && s.meetingTitleColor) setMeetingTitleColor(s.meetingTitleColor);
            else if (typeof s.textColor === 'string' && s.textColor) setMeetingTitleColor(s.textColor);
            if (typeof s.showTitle === 'boolean') setShowTitle(s.showTitle);
            if (typeof s.showTimeRange === 'boolean') setShowTimeRange(s.showTimeRange);
            if (typeof s.slideshowPriority === 'boolean') setSlideshowPriority(s.slideshowPriority);
            if (typeof s.demoRatio === 'string' && /^\d+\s*:\s*\d+$/.test(s.demoRatio)) setDemoRatio(String(s.demoRatio));
            if (typeof s.videoGroupId === 'number') setVideoGroupId(String(s.videoGroupId));
            if (typeof s.slideshowGroupId === 'number') setSlideshowGroupId(String(s.slideshowGroupId));
            if (Array.isArray(s.slideshowGroupIds)) {
                setSlideshowGroupIds(
                    s.slideshowGroupIds
                        .map((x) => Number(x))
                        .filter((n) => Number.isFinite(n) && n > 0)
                        .map((n) => Math.floor(n))
                );
            }
            if (s.displayArrows && typeof s.displayArrows === 'object') setDisplayArrows(s.displayArrows);
            const deviceName = displaysList.find(d => d.id === sourceId)?.description || `Device #${sourceId}`;
            alert(`Runtime colours and background copied from ${deviceName}. Don't forget to Save.`);
            setCopyRuntimeFromId(''); // Reset dropdown
        } catch (e) {
            console.error('[EditDisplay_modal] Failed to copy runtime settings from device:', e);
            alert('Cannot load runtime settings from this device. Check ID or permissions.');
        }
    };

    const handleNameChange = (event) => {
        setName(event.target.value);
    };

    const handleTypeChange = (event) => {
        setType(event.target.value);
    };

    const handleRoomGroupChange = (event) => {
        const v = event.target.value;
        setRoomGroup(v === "" || v === null ? null : Number(v));
    };

    const close_handler = () => {
        props.close();
    };

    const handleSubmit = async () => {
        const nameToSend = String(name || '').trim();
        if (!nameToSend || !type) {
            alert("Please fill in all required fields");
            return;
        }

        setLoading(true);
        try {
            // Helper function to safely parse integer or return null
            const parseIntOrNull = (val) => {
                if (!val || val === "" || val === null || val === undefined) return null;
                const trimmed = String(val).trim();
                if (trimmed === "") return null;
                const parsed = parseInt(trimmed, 10);
                return isNaN(parsed) || !isFinite(parsed) ? null : parsed;
            };
            
            // roomGroup must be a number (groupId) or null - never string like 'A-D'
            const roomGroupNum = parseIntOrNull(roomGroup) ?? (typeof roomGroup === 'number' && !isNaN(roomGroup) ? roomGroup : null);
            
            const payload = {
                name: nameToSend,
                type: type,
                organization: props.orgId,
                roomGroup: roomGroupNum,
                url_id: parseIntOrNull(urlId),
                url_room: parseIntOrNull(urlRoom),
                url_orgID: (urlOrgID && urlOrgID.trim() !== "") ? urlOrgID.trim() : null,
                url_kiosk: parseIntOrNull(urlKiosk),
            };
            
            console.log('[EditDisplay_modal] Sending payload:', payload);
            
            await axios.put(`${path}/updateDisplay/${props.display.id}`, payload);

            // Save runtime settings (per-device) only when user is allowed to edit kiosk settings
                if (allowKioskSettings) {
                const settingsPayload = {
                    objectFit: objectFit === 'cover' ? 'cover' : 'contain',
                    showLogo: Boolean(showLogo),
                    backgroundColor: String(backgroundColor || '#012e5f'),
                    roomNameColor: String(roomNameColor || '#ffffff'),
                    meetingTitleColor: String(meetingTitleColor || '#ffffff'),
                    showTitle: Boolean(showTitle),
                    showTimeRange: Boolean(showTimeRange),
                    slideshowPriority: Boolean(slideshowPriority),
                    demoRatio: String(demoRatio || '9:16'),
                    videoGroupId: videoGroupId ? Number(videoGroupId) : null,
                    slideshowGroupId: slideshowGroupId ? Number(slideshowGroupId) : null,
                    slideshowGroupIds: Array.isArray(slideshowGroupIds) ? slideshowGroupIds : [],
                    displayArrows: displayArrows || {},
                };
                const orgId = props.orgId || props.display?.cOrganization_id;
                console.log('[EditDisplay_modal] Saving device settings:', { deviceId: props.display.id, displayArrows: settingsPayload.displayArrows });
                await axios.put(`${path}/setDeviceSettings/${props.display.id}`, { settings: settingsPayload, orgID: orgId });
            }

            alert("Display updated successfully");
            // Reload display data to get updated URL parameters
            if (props.onUpdate) {
                props.onUpdate();
            }
            props.close();
        } catch (error) {
            const status = error?.response?.status;
            const data = error?.response?.data;
            if (status === 409 && data?.missingTable) {
                alert(
                    `Cannot save runtime settings because database table is missing: ${data.missingTable}\n\n` +
                    `${data.hint || 'Run the migration SQL on the production database.'}`
                );
            } else {
                alert("Error updating display");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={true}
            onClose={close_handler}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: { xs: 0, sm: "12px" },
                    maxHeight: { xs: "100vh", sm: "90vh" },
                    margin: { xs: 0, sm: "32px" },
                    width: { xs: "100%", sm: "auto" },
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: { xs: "18px", sm: "20px" },
                    fontWeight: 600,
                    pb: 1.5,
                    borderBottom: "1px solid #e0e0e0",
                }}
            >
                Edit Display
            </DialogTitle>

            <DialogContent
                sx={{
                    pt: 3,
                    pb: 3,
                    px: { xs: 2, sm: 3 },
                    overflowY: 'auto',
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 2,
                        paddingTop: 2,
                        width: "100%",
                        "& > *": { minWidth: 0 },
                    }}
                >
                    <TextField
                        id="name"
                        label="Device Name"
                        variant="outlined"
                        value={name}
                        onChange={handleNameChange}
                        helperText="Display/Kiosk name shown in admin."
                        sx={{ gridColumn: { xs: 1, sm: "span 2" } }}
                    />
                    <FormControl sx={{ width: '100%' }}>
                        <InputLabel id="device-type-label">Device Type</InputLabel>
                        <Select
                            labelId="device-type-label"
                            value={type}
                            label="Device Type"
                            onChange={handleTypeChange}
                        >
                            <MenuItem value={1}>Display</MenuItem>
                            <MenuItem value={2}>Kiosk</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl sx={{ width: '100%' }}>
                        <InputLabel id="room-group-label">Card group</InputLabel>
                        <Select
                            labelId="room-group-label"
                            value={roomGroup ?? ""}
                            label="Card group"
                            onChange={handleRoomGroupChange}
                        >
                            <MenuItem value="">None</MenuItem>
                            {groups.map((g) => (
                                <MenuItem key={g.groupId} value={g.groupId}>{g.groupName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* <Box sx={{ gridColumn: "1 / -1", display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
                        <Divider sx={{ flex: 1 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, px: 1 }}>URL Parameters</Typography>
                        <Divider sx={{ flex: 1 }} />
                    </Box>

                    <TextField
                        id="url_id"
                        label="URL ID"
                        variant="outlined"
                        type="number"
                        value={urlId}
                        onChange={(e) => setUrlId(e.target.value)}
                        helperText="e.g., 1, 3, 9, 11, 12, 13"
                        sx={{ gridColumn: { xs: 1, sm: 1 } }}
                    />
                    <TextField
                        id="url_room"
                        label="URL Room"
                        variant="outlined"
                        type="number"
                        value={urlRoom}
                        onChange={(e) => setUrlRoom(e.target.value)}
                        helperText="e.g., 23, 24, 25, 26"
                        sx={{ gridColumn: { xs: 1, sm: 2 } }}
                    />
                    <TextField
                        id="url_orgID"
                        label="URL OrgID"
                        variant="outlined"
                        value={urlOrgID}
                        onChange={(e) => setUrlOrgID(e.target.value)}
                        helperText="e.g., E15F8010-B7EB-4526-A985-F00D517EBF4C (leave empty for NULL)"
                        sx={{ gridColumn: { xs: 1, sm: "span 2" } }}
                    />
                    <TextField
                        id="url_kiosk"
                        label="URL Kiosk"
                        variant="outlined"
                        type="number"
                        value={urlKiosk}
                        onChange={(e) => setUrlKiosk(e.target.value)}
                        helperText="e.g., 1, 2 (for ViennaHouse kiosks)"
                        sx={{ gridColumn: { xs: 1, sm: "span 2" } }}
                    /> */}

                    {showRuntimeSection && (
                        <>
                            <Box sx={{ gridColumn: "1 / -1", display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
                                <Divider sx={{ flex: 1 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, px: 1 }}>Runtime</Typography>
                                <Divider sx={{ flex: 1 }} />
                            </Box>

                            {displaysList.length > 0 && (
                                <Box sx={{ gridColumn: "1 / -1", mb: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Copy colours & background from another device
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        <FormControl sx={{ minWidth: 200 }}>
                                            <InputLabel id="copy-from-device-label">Copy from device</InputLabel>
                                            <Select
                                                labelId="copy-from-device-label"
                                                label="Copy from device"
                                                value={copyRuntimeFromId}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    if (v) {
                                                        handleCopyRuntimeFromDevice(v);
                                                    } else {
                                                        setCopyRuntimeFromId('');
                                                    }
                                                }}
                                            >
                                                <MenuItem value="">
                                                    <em>None</em>
                                                </MenuItem>
                                                {displaysList
                                                    .filter((d) => Number(d.device_group) === Number(type) && d.id !== props.display?.id)
                                                    .map((d) => (
                                                        <MenuItem key={d.id} value={d.id}>
                                                            {d.description || d.name || `Device #${d.id}`}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Box>
                            )}

                            <FormControlLabel
                                control={<Switch size="small" checked={showLogo} onChange={(e) => setShowLogo(e.target.checked)} />}
                                label="Show logo"
                                sx={{ gridColumn: { xs: 1, sm: 1 } }}
                            />
                            <FormControlLabel
                                control={<Switch size="small" checked={showTitle} onChange={(e) => setShowTitle(e.target.checked)} />}
                                label="Show title"
                                sx={{ gridColumn: { xs: 1, sm: 1 } }}
                            />
                            <FormControlLabel
                                control={<Switch size="small" checked={showTimeRange} onChange={(e) => setShowTimeRange(e.target.checked)} />}
                                label="Show start–end time"
                                sx={{ gridColumn: { xs: 1, sm: 1 } }}
                            />
                            <FormControlLabel
                                control={<Switch size="small" checked={slideshowPriority} onChange={(e) => setSlideshowPriority(e.target.checked)} />}
                                label="Slideshow priority (override meetings)"
                                sx={{ gridColumn: { xs: 1, sm: 1 } }}
                            />
                            {isKiosk && (
                                <>
                                    <FormControl sx={{ width: '100%' }}>
                                        <InputLabel id="video-group-id-label">Video group</InputLabel>
                                        <Select
                                            labelId="video-group-id-label"
                                            label="Video group"
                                            value={videoGroupId}
                                            onChange={(e) => setVideoGroupId(e.target.value)}
                                        >
                                            <MenuItem value=""><em>None</em></MenuItem>
                                            {groups.map((g) => (
                                                <MenuItem key={`v-${g.groupId}`} value={String(g.groupId)}>
                                                    {`${g.groupName} (#${g.groupId})`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl sx={{ width: '100%' }}>
                                        <InputLabel id="slideshow-group-id-label">Slideshow group</InputLabel>
                                        <Select
                                            labelId="slideshow-group-id-label"
                                            label="Slideshow group"
                                            value={slideshowGroupId}
                                            onChange={(e) => setSlideshowGroupId(e.target.value)}
                                        >
                                            <MenuItem value=""><em>Use device roomGroup</em></MenuItem>
                                            {groups.map((g) => (
                                                <MenuItem key={`s-${g.groupId}`} value={String(g.groupId)}>
                                                    {`${g.groupName} (#${g.groupId})`}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        label="Assigned groups (from Edit group)"
                                        value={(Array.isArray(slideshowGroupIds) ? slideshowGroupIds : [])
                                            .map((id) => groups.find((g) => Number(g.groupId) === Number(id))?.groupName || `#${id}`)
                                            .join(', ')}
                                        InputProps={{ readOnly: true }}
                                        helperText="This list is managed in Edit group and used for multi-group slideshow cycle."
                                        sx={{ width: '100%' }}
                                    />
                                </>
                            )}
                            {isKiosk && displaysList.filter(d => Number(d.device_group) === 1).length > 0 && (
                                <Box sx={{ gridColumn: "1 / -1" }}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                        Display arrows (meeting list)
                                    </Typography>
                                    {displaysList
                                        .filter(d => Number(d.device_group) === 1)
                                        .map(d => {
                                            const did = String(d.id);
                                            const hasArrow = did in displayArrows;
                                            const dir = displayArrows[did] || 'right';
                                            return (
                                                <Box key={did} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                                    <Switch
                                                        size="small"
                                                        checked={hasArrow}
                                                        onChange={(e) => {
                                                            setDisplayArrows(prev => {
                                                                const next = { ...prev };
                                                                if (e.target.checked) next[did] = 'right';
                                                                else delete next[did];
                                                                return next;
                                                            });
                                                        }}
                                                    />
                                                    <Typography variant="body2" sx={{ minWidth: 120 }}>
                                                        {d.description || `Display #${d.id}`}
                                                    </Typography>
                                                    {hasArrow && (
                                                        <FormControl size="small" sx={{ minWidth: 130 }}>
                                                            <Select
                                                                value={dir}
                                                                onChange={(e) => {
                                                                    setDisplayArrows(prev => ({ ...prev, [did]: e.target.value }));
                                                                }}
                                                            >
                                                                <MenuItem value="right">Right →</MenuItem>
                                                                <MenuItem value="left">Left ←</MenuItem>
                                                                <MenuItem value="up">Up ↑</MenuItem>
                                                                <MenuItem value="down">Down ↓</MenuItem>
                                                                <MenuItem value="up-right">Up-right ↗</MenuItem>
                                                                <MenuItem value="up-left">Up-left ↖</MenuItem>
                                                                <MenuItem value="down-right">Down-right ↘</MenuItem>
                                                                <MenuItem value="down-left">Down-left ↙</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                </Box>
                            )}

                            <Box sx={{ gridColumn: "1 / -1", display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Background</Typography>
                                <input
                                    type="color"
                                    value={backgroundColor}
                                    onChange={(e) => setBackgroundColor(e.target.value)}
                                    style={{ width: 40, height: 32, padding: 2, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}
                                />
                                <TextField
                                    size="small"
                                    value={backgroundColor}
                                    onChange={(e) => setBackgroundColor(e.target.value)}
                                    sx={{ flex: 1, minWidth: 0 }}
                                />
                            </Box>
                            <Box sx={{ gridColumn: "1 / -1", display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Room name colour</Typography>
                                <input
                                    type="color"
                                    value={roomNameColor}
                                    onChange={(e) => setRoomNameColor(e.target.value)}
                                    style={{ width: 40, height: 32, padding: 2, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}
                                />
                                <TextField
                                    size="small"
                                    value={roomNameColor}
                                    onChange={(e) => setRoomNameColor(e.target.value)}
                                    sx={{ flex: 1, minWidth: 0 }}
                                />
                            </Box>
                            <Box sx={{ gridColumn: "1 / -1", display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Meeting title colour</Typography>
                                <input
                                    type="color"
                                    value={meetingTitleColor}
                                    onChange={(e) => setMeetingTitleColor(e.target.value)}
                                    style={{ width: 40, height: 32, padding: 2, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}
                                />
                                <TextField
                                    size="small"
                                    value={meetingTitleColor}
                                    onChange={(e) => setMeetingTitleColor(e.target.value)}
                                    sx={{ flex: 1, minWidth: 0 }}
                                />
                            </Box>
                        </>
                    )}
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2.5 },
                    pt: 2,
                    borderTop: "1px solid #e0e0e0",
                    gap: { xs: 1, sm: 1.5 },
                    flexDirection: { xs: "column", sm: "row" },
                }}
            >
                <Button
                    onClick={close_handler}
                    disabled={loading}
                    variant="outlined"
                    sx={{
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { xs: "100%", sm: 120 },
                        height: { xs: "48px", sm: "auto" },
                        order: { xs: 2, sm: 1 },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    disabled={!name.trim() || !type || loading}
                    onClick={handleSubmit}
                    variant="contained"
                    sx={{
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { xs: "100%", sm: 120 },
                        height: { xs: "48px", sm: "auto" },
                        fontWeight: 600,
                        order: { xs: 1, sm: 2 },
                        boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                        "&:hover": {
                            boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
                        },
                        "&:disabled": {
                            backgroundColor: "#e0e0e0",
                            color: "#9e9e9e",
                        },
                    }}
                >
                    {loading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CircularProgress size={20} color="inherit" />
                            <span>Saving...</span>
                        </Box>
                    ) : (
                        "Save"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditDisplay_modal;
